import { useState, useCallback, useRef } from "react";
import useAgentStore from "./useAgentStore";

const ACCEPTED_EXTENSIONS = [
  ".csv", ".json", ".pdf", ".xlsx", ".xls", ".txt", ".md",
  ".docx", ".pptx", ".png", ".jpg", ".jpeg", ".gif", ".webp",
  ".svg", ".html", ".xml", ".yaml", ".yml",
];

export const ACCEPT_STRING = ACCEPTED_EXTENSIONS.join(",");
export const MAX_FILES = 5;
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

function isAcceptedFile(file) {
  const name = (file.name || "").toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function formatFileSize(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const IMAGE_MIME_TYPES = new Set([
  "image/png", "image/jpeg", "image/gif", "image/webp",
]);

const VISION_MODEL_PREFIXES = [
  "gpt-", "o1-", "o3", "o4-",
  "claude-",
];

export function isVisionCapableModel(model) {
  if (!model) return false;
  return VISION_MODEL_PREFIXES.some((prefix) => model.startsWith(prefix));
}

export function isImageAttachment(attachment) {
  return IMAGE_MIME_TYPES.has(attachment.mimeType);
}

export function separateAttachments(attachments, isVisionModel) {
  const ready = attachments.filter((a) => a.fileId && !a.error);
  if (!isVisionModel) {
    return { images: [], files: ready };
  }
  const images = ready.filter((a) => isImageAttachment(a));
  const files = ready.filter((a) => !isImageAttachment(a));
  return { images, files };
}

export function buildAttachmentBlock(attachments) {
  if (attachments.length === 0) return "";

  const lines = attachments.map((a) => {
    const size = formatFileSize(a.size);
    const ext = (a.fileName || "").split(".").pop()?.toUpperCase() || "";
    const meta = [ext, size].filter(Boolean).join(", ");
    return `- ${a.fileName}${meta ? ` (${meta})` : ""}: ${a.downloadUrl}`;
  });

  return `\n\n[Attached files]\n${lines.join("\n")}`;
}

export default function useAttachments(idOrOptions) {
  const agentId =
    typeof idOrOptions === "string"
      ? idOrOptions
      : idOrOptions?.agentId || null;

  const [attachments, setAttachments] = useState([]);
  const [warning, setWarning] = useState("");
  const fileInputRef = useRef(null);

  const addFiles = useCallback(
    (files) => {
      setWarning("");
      const accepted = Array.from(files).filter(isAcceptedFile);
      if (accepted.length === 0) return;

      // Enforce per-file size limit
      const withinSize = [];
      const oversized = [];
      for (const f of accepted) {
        if (f.size > MAX_FILE_SIZE_BYTES) {
          oversized.push(f.name);
        } else {
          withinSize.push(f);
        }
      }
      if (oversized.length > 0) {
        setWarning(`${oversized.length > 1 ? "Files" : "File"} too large (max 10 MB): ${oversized.join(", ")}`);
      }
      if (withinSize.length === 0) return;

      // Enforce max file count
      setAttachments((prev) => {
        const remaining = MAX_FILES - prev.length;
        if (remaining <= 0) {
          setWarning(`Maximum ${MAX_FILES} files per message`);
          return prev;
        }
        const toAdd = withinSize.slice(0, remaining);
        if (toAdd.length < withinSize.length) {
          setWarning(`Maximum ${MAX_FILES} files per message`);
        }

        const newEntries = toAdd.map((file) => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          fileName: file.name,
          size: file.size,
          mimeType: file.type,
          uploading: true,
          fileId: null,
          downloadUrl: null,
          error: null,
        }));

        // Kick off uploads outside setState
        setTimeout(() => {
          newEntries.forEach((entry) => {
            const uploadPromise = useAgentStore
              .getState()
              .uploadChatAttachment(entry.file, agentId);

            uploadPromise
              .then((result) => {
                setAttachments((p) =>
                  p.map((a) =>
                    a.id === entry.id
                      ? {
                          ...a,
                          uploading: false,
                          fileId: result.fileId,
                          fileName: result.fileName,
                          downloadUrl: result.downloadUrl,
                          mimeType: result.mimeType,
                          size: result.size,
                        }
                      : a
                  )
                );
              })
              .catch(() => {
                setAttachments((p) =>
                  p.map((a) =>
                    a.id === entry.id
                      ? { ...a, uploading: false, error: "Upload failed" }
                      : a
                  )
                );
              });
          });
        }, 0);

        return [...prev, ...newEntries];
      });
    },
    [agentId]
  );

  const removeAttachment = useCallback(
    (id) => {
      setAttachments((prev) => {
        const target = prev.find((a) => a.id === id);
        if (target?.fileId) {
          useAgentStore
            .getState()
            .deleteChatAttachment(target.fileId, agentId);
        }
        return prev.filter((a) => a.id !== id);
      });
    },
    [agentId]
  );

  const clearAttachments = useCallback(() => {
    setAttachments([]);
    setWarning("");
  }, []);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e) => {
      if (e.target.files?.length) {
        addFiles(e.target.files);
      }
      e.target.value = "";
    },
    [addFiles]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.files?.length) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const hasAttachments = attachments.length > 0;
  const isUploading = attachments.some((a) => a.uploading);

  return {
    attachments,
    hasAttachments,
    isUploading,
    warning,
    fileInputRef,
    addFiles,
    removeAttachment,
    clearAttachments,
    openFilePicker,
    handleFileInputChange,
    handleDrop,
    handleDragOver,
  };
}
